import {Search, Clock, Users, Building2, ClipboardList, FileText} from 'lucide-react'
import React, {useState, useCallback, useRef, useEffect, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {SearchInput} from '~/src/design-system-shims/SearchInput'
import {useLazyGlobalSearchQuery} from '~/src/store/apis/global-search-api'
import type {
  CandidateSearchResult,
  CompanySearchResult,
  JobOrderSearchResult,
  ServiceOrderSearchResult,
} from '~/src/store/apis/global-search-api'
import {useCurrentUser} from '~/src/store/slice-hooks'

const RECENT_SEARCHES_KEY = 'staffology_recent_searches'
const MAX_RECENT = 5
const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 300

const PopoverWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

const PopoverPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1300;
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`

const SearchButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  color: var(--brand-cta);
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: var(--brand-hover);
    background-color: rgba(3, 169, 252, 0.08);
  }
`

const SearchContainer = styled.div`
  width: 420px;
  max-height: 480px;
  overflow-y: auto;
  background: var(--white);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
`

const SearchInputWrapper = styled.div`
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid #f0f0f0;
`

const ResultsBody = styled.div`
  padding: var(--space-2) 0;
`

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-5) var(--space-1);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--neutral-600);
`

const ResultItem = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px var(--space-5);
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s;

  &:hover {
    background-color: rgba(3, 169, 252, 0.06);
  }
`

const ResultTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #334d6e;
`

const ResultSubtitle = styled.span`
  font-size: 12px;
  color: var(--neutral-600);
`

const EmptyMessage = styled.div`
  padding: var(--space-8) var(--space-5);
  text-align: center;
  font-size: 14px;
  color: var(--neutral-600);
`

const RecentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-5) var(--space-1);
`

const RecentTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--neutral-600);
`

const ClearAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 11px;
  color: var(--brand-cta);
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`

const RecentItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: var(--space-2) var(--space-5);
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: #334d6e;
  transition: background-color 0.15s;

  &:hover {
    background-color: rgba(3, 169, 252, 0.06);
  }
`

const LoadingDots = styled.div`
  padding: var(--space-6) var(--space-5);
  text-align: center;
  font-size: 13px;
  color: var(--neutral-600);
`

function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function saveRecentSearch(term: string): void {
  const recent = getRecentSearches().filter((t) => t.toLowerCase() !== term.toLowerCase())
  recent.unshift(term)
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

function clearRecentSearches(): void {
  localStorage.removeItem(RECENT_SEARCHES_KEY)
}

const COMPANY_ROLES = ['companyadmin', 'companyuser']

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate()
  const {currentUser} = useCurrentUser()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const isCompanyUser = useMemo(
    () => currentUser?.roles?.some((r) => COMPANY_ROLES.includes(r.toLowerCase())) ?? false,
    [currentUser?.roles],
  )

  const [triggerSearch, {data: searchData, isFetching}] = useLazyGlobalSearchQuery()

  const handleOpen = useCallback(() => {
    setOpen(true)
    setRecentSearches(getRecentSearches())
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setInputValue('')
  }, [])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!popoverRef.current?.contains(e.target as Node)) handleClose()
    }
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', escHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', escHandler)
    }
  }, [open, handleClose])

  const performSearch = useCallback(
    (query: string) => {
      if (query.length >= MIN_QUERY_LENGTH) {
        void triggerSearch({query, limit: 5})
      }
    },
    [triggerSearch],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setInputValue(value)

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      debounceRef.current = setTimeout(() => {
        performSearch(value)
      }, DEBOUNCE_MS)
    },
    [performSearch],
  )

  const handleClearInput = useCallback(() => {
    setInputValue('')
    inputRef.current?.focus()
  }, [])

  const handleClearRecent = useCallback(() => {
    clearRecentSearches()
    setRecentSearches([])
  }, [])

  const handleRecentClick = useCallback(
    (term: string) => {
      setInputValue(term)
      performSearch(term)
    },
    [performSearch],
  )

  const navigateTo = useCallback(
    (path: string, searchTerm: string) => {
      saveRecentSearch(searchTerm)
      handleClose()
      navigate(path)
    },
    [navigate, handleClose],
  )

  const handleCandidateClick = useCallback(
    (candidate: CandidateSearchResult) => {
      navigateTo(
        `/candidates?firstName=${encodeURIComponent(
          candidate.firstName,
        )}&lastName=${encodeURIComponent(candidate.lastName)}`,
        inputValue,
      )
    },
    [navigateTo, inputValue],
  )

  const handleCompanyClick = useCallback(
    (company: CompanySearchResult) => {
      navigateTo(`/manage-companies/${company.id}`, inputValue)
    },
    [navigateTo, inputValue],
  )

  const handleJobOrderClick = useCallback(
    (jobOrder: JobOrderSearchResult) => {
      navigateTo(`/manage-job-orders/summary/${encodeURIComponent(jobOrder.id)}`, inputValue)
    },
    [navigateTo, inputValue],
  )

  const handleServiceOrderClick = useCallback(
    (serviceOrder: ServiceOrderSearchResult) => {
      navigateTo(`/manage-orders/${encodeURIComponent(serviceOrder.id)}`, inputValue)
    },
    [navigateTo, inputValue],
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const hasQuery = inputValue.length >= MIN_QUERY_LENGTH
  const hasResults =
    searchData &&
    (searchData.candidates.length > 0 ||
      searchData.companies.length > 0 ||
      searchData.jobOrders.length > 0 ||
      searchData.serviceOrders.length > 0)

  const showRecent = !hasQuery && recentSearches.length > 0
  const showNoResults = hasQuery && !isFetching && searchData && !hasResults
  const showResults = hasQuery && !isFetching && hasResults

  return (
    <PopoverWrapper ref={popoverRef}>
      <SearchButton onClick={handleOpen} aria-label="Global search" data-testid="global-search-btn">
        <Search size={20} />
      </SearchButton>

      {open && (
        <PopoverPanel>
          <SearchContainer data-testid="global-search-popover">
            <SearchInputWrapper>
              <SearchInput
                ref={inputRef}
                placeholder={
                  isCompanyUser
                    ? 'Search orders, company...'
                    : 'Search candidates, clients, orders...'
                }
                value={inputValue}
                onChange={handleInputChange}
                onClear={handleClearInput}
                data-testid="global-search-input"
                style={{width: '100%'}}
              />
            </SearchInputWrapper>

            <ResultsBody>
              {showRecent && (
                <>
                  <RecentHeader>
                    <RecentTitle>Recent searches</RecentTitle>
                    <ClearAllButton onClick={handleClearRecent}>Clear all</ClearAllButton>
                  </RecentHeader>
                  {recentSearches.map((term) => (
                    <RecentItem key={term} onClick={() => handleRecentClick(term)}>
                      <Clock size={14} color="var(--neutral-600)" />
                      {term}
                    </RecentItem>
                  ))}
                </>
              )}

              {hasQuery && isFetching && <LoadingDots>Searching...</LoadingDots>}

              {showNoResults && <EmptyMessage>No matches found.</EmptyMessage>}

              {showResults && searchData && (
                <>
                  {searchData.candidates.length > 0 && (
                    <>
                      <GroupHeader>
                        <Users size={14} />
                        Candidates
                      </GroupHeader>
                      {searchData.candidates.map((c) => (
                        <ResultItem
                          key={c.id}
                          onClick={() => handleCandidateClick(c)}
                          data-testid={`search-result-candidate-${c.id}`}>
                          <ResultTitle>
                            {c.firstName} {c.lastName}
                          </ResultTitle>
                          <ResultSubtitle>
                            {[
                              c.branch,
                              c.last4SSN ? `SSN: ***${c.last4SSN}` : null,
                              c.city && c.state ? `${c.city}, ${c.state}` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </ResultSubtitle>
                        </ResultItem>
                      ))}
                    </>
                  )}

                  {searchData.companies.length > 0 && (
                    <>
                      <GroupHeader>
                        <Building2 size={14} />
                        Clients
                      </GroupHeader>
                      {searchData.companies.map((c) => (
                        <ResultItem
                          key={c.id}
                          onClick={() => handleCompanyClick(c)}
                          data-testid={`search-result-company-${c.id}`}>
                          <ResultTitle>{c.name}</ResultTitle>
                          <ResultSubtitle>
                            {c.city && c.state ? `${c.city}, ${c.state}` : ''}
                          </ResultSubtitle>
                        </ResultItem>
                      ))}
                    </>
                  )}

                  {searchData.jobOrders.length > 0 && (
                    <>
                      <GroupHeader>
                        <ClipboardList size={14} />
                        Job Orders
                      </GroupHeader>
                      {searchData.jobOrders.map((jo) => (
                        <ResultItem
                          key={jo.id}
                          onClick={() => handleJobOrderClick(jo)}
                          data-testid={`search-result-joborder-${jo.id}`}>
                          <ResultTitle>#{jo.orderNumber}</ResultTitle>
                          <ResultSubtitle>
                            {[jo.companyName, jo.status].filter(Boolean).join(' · ')}
                          </ResultSubtitle>
                        </ResultItem>
                      ))}
                    </>
                  )}

                  {searchData.serviceOrders.length > 0 && (
                    <>
                      <GroupHeader>
                        <FileText size={14} />
                        Service Orders
                      </GroupHeader>
                      {searchData.serviceOrders.map((so) => (
                        <ResultItem
                          key={so.id}
                          onClick={() => handleServiceOrderClick(so)}
                          data-testid={`search-result-serviceorder-${so.id}`}>
                          <ResultTitle>#{so.orderNumber}</ResultTitle>
                          <ResultSubtitle>
                            {[so.companyName, so.companyStatus].filter(Boolean).join(' \u00B7 ')}
                          </ResultSubtitle>
                        </ResultItem>
                      ))}
                    </>
                  )}
                </>
              )}

              {!showRecent && !hasQuery && !isFetching && (
                <EmptyMessage>Type at least 3 characters to search</EmptyMessage>
              )}
            </ResultsBody>
          </SearchContainer>
        </PopoverPanel>
      )}
    </PopoverWrapper>
  )
}

export default GlobalSearch
